import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { 
  QrCode, Download, Printer, Wifi, DollarSign, 
  Link as LinkIcon, Type, Copy, Store, UtensilsCrossed, Palette
} from 'lucide-react';
import toast from 'react-hot-toast';

const QRCodeManager = ({ lojaInfo }) => {
  const [tipo, setTipo] = useState('cardapio'); // 'cardapio' | 'pix' | 'wifi' | 'custom'
  const qrRef = useRef();

  // Customizações de Impressão / Display
  const [numeroMesa, setNumeroMesa] = useState('');
  const [tituloDisplay, setTituloDisplay] = useState('');
  const [subtituloDisplay, setSubtituloDisplay] = useState('');
  const [corPrimaria, setCorPrimaria] = useState('#2563eb');
  const [incluirLogo, setIncluirLogo] = useState(true);

  // Estados dos formulários específicos
  const [pixChave, setPixChave] = useState('');
  const [pixNome, setPixNome] = useState('');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiSenha, setWifiSenha] = useState('');
  const [wifiTipo, setWifiTipo] = useState('WPA');
  const [textoLivre, setTextoLivre] = useState('');

  const BASE_URL = 'https://app.universomidiadigital.com.br';
  const logoFinal = lojaInfo?.logo_url?.startsWith('http')
    ? lojaInfo.logo_url
    : lojaInfo?.logo_url ? `${BASE_URL}${lojaInfo.logo_url}` : null;

  // Monta a URL base do cardápio público (com mesa se configurado)
  const getUrlCardapio = () => {
    if (!lojaInfo?.slug) return 'https://app.universomidiadigital.com.br';
    const baseUrl = `https://app.universomidiadigital.com.br/${lojaInfo.slug}`;
    return numeroMesa ? `${baseUrl}?mesa=${encodeURIComponent(numeroMesa)}` : baseUrl;
  };

  // Gera a payload de acordo com a aba selecionada
  const getQRCodeValue = () => {
    switch (tipo) {
      case 'cardapio':
        return getUrlCardapio();
      case 'wifi':
        if (!wifiSsid) return '';
        return `WIFI:S:${wifiSsid};T:${wifiTipo};P:${wifiSenha};;`;
      case 'pix':
        return pixChave;
      case 'custom':
        return textoLivre;
      default:
        return '';
    }
  };

  const qrValor = getQRCodeValue();

  // Títulos padrão caso o usuário não personalize
  const getTituloExibicao = () => {
    if (tituloDisplay) return tituloDisplay;
    if (tipo === 'cardapio') return numeroMesa ? `Cardápio - Mesa ${numeroMesa}` : (lojaInfo?.nome || 'Cardápio Digital');
    if (tipo === 'wifi') return 'Wi-Fi do Estabelecimento';
    if (tipo === 'pix') return 'Pagamento via Pix';
    return lojaInfo?.nome || 'Acesse Aqui';
  };

  const getSubtituloExibicao = () => {
    if (subtituloDisplay) return subtituloDisplay;
    if (tipo === 'cardapio') return 'Aponte a câmera do seu celular para fazer seu pedido';
    if (tipo === 'wifi') return 'Conecte-se à nossa rede sem fio';
    if (tipo === 'pix') return pixNome ? `Favorecido: ${pixNome}` : 'Escaneie para transferir ou pagar';
    return 'Escaneie o código com a câmera do seu celular';
  };

  const baixarQRCode = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error('Gere um QR Code válido primeiro.');
      return;
    }
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrcode-${tipo}-${numeroMesa ? `mesa-${numeroMesa}-` : ''}${lojaInfo?.slug || 'estabelecimento'}.png`;
    link.click();
    toast.success('Download iniciado!');
  };

  const imprimirDisplay = () => {
    if (!qrValor) {
      toast.error('Preencha os dados do QR Code antes de imprimir.');
      return;
    }
    window.print();
  };

  const copiarConteudo = () => {
    if (!qrValor) return;
    navigator.clipboard.writeText(qrValor);
    toast.success('Conteúdo copiado!');
  };

  return (
    <div className="qrcode-manager-container" style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      
      {/* Estilos específicos para a impressão (Display de Mesa A4 / Totem) */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .printable-card, .printable-card * {
            visibility: visible;
          }
          .printable-card {
            position: fixed;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 380px !important;
            border: 2px solid #000 !important;
            padding: 32px !important;
            box-shadow: none !important;
            border-radius: 16px !important;
            background: #fff !important;
            page-break-inside: avoid;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* Header (Não imprime) */}
      <div className="no-print" style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
          <QrCode size={26} color="#2563eb" /> Gerador & Impressão de QR Codes
        </h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
          Gere displays prontos para impressão em acrílico, placas de mesa, conexões Wi-Fi e Pix.
        </p>
      </div>

      {/* Seleção de Abas (Não imprime) */}
      <div className="no-print" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {[
          { id: 'cardapio', label: 'Cardápio / Mesas', icon: <UtensilsCrossed size={16} /> },
          { id: 'wifi', label: 'Conexão Wi-Fi', icon: <Wifi size={16} /> },
          { id: 'pix', label: 'Chave Pix', icon: <DollarSign size={16} /> },
          { id: 'custom', label: 'Link / Texto Livre', icon: <Type size={16} /> }
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => {
              setTipo(item.id);
              setTituloDisplay('');
              setSubtituloDisplay('');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              borderRadius: '8px',
              border: tipo === item.id ? '2px solid #2563eb' : '1px solid #cbd5e1',
              backgroundColor: tipo === item.id ? '#eff6ff' : '#ffffff',
              color: tipo === item.id ? '#2563eb' : '#475569',
              fontWeight: tipo === item.id ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', alignItems: 'start' }}>
        
        {/* Painel de Controles (Não imprime) */}
        <div className="no-print" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Formulário do Tipo */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', marginBottom: '16px' }}>
              1. Conteúdo do QR Code
            </h3>

            {tipo === 'cardapio' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Número ou Identificação da Mesa (Opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 01, 15, Balcão, Área Externa"
                    value={numeroMesa}
                    onChange={(e) => setNumeroMesa(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Ao definir a mesa, o sistema identifica de onde o pedido foi aberto.
                  </span>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Link Final de Acesso
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={getUrlCardapio()}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#475569', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {tipo === 'wifi' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Nome da Rede (SSID)
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Clientes_Restaurante"
                    value={wifiSsid}
                    onChange={(e) => setWifiSsid(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Senha da Rede
                  </label>
                  <input
                    type="text"
                    placeholder="Digite a senha"
                    value={wifiSenha}
                    onChange={(e) => setWifiSenha(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Segurança
                  </label>
                  <select
                    value={wifiTipo}
                    onChange={(e) => setWifiTipo(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  >
                    <option value="WPA">WPA / WPA2 / WPA3</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">Sem Senha (Aberta)</option>
                  </select>
                </div>
              </div>
            )}

            {tipo === 'pix' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Nome do Beneficiário / Loja
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Lanches Super Mania"
                    value={pixNome}
                    onChange={(e) => setPixNome(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Código Pix Copia e Cola / Chave
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Cole o código Pix Copia e Cola ou Chave Pix"
                    value={pixChave}
                    onChange={(e) => setPixChave(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {tipo === 'custom' && (
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Link ou Texto
                </label>
                <input
                  type="text"
                  placeholder="https://instagram.com/sualoja ou texto"
                  value={textoLivre}
                  onChange={(e) => setTextoLivre(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>

          {/* Personalização Visual da Placa */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Palette size={18} color="#2563eb" /> 2. Personalização do Display
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Título Superior da Placa
                </label>
                <input
                  type="text"
                  placeholder={getTituloExibicao()}
                  value={tituloDisplay}
                  onChange={(e) => setTituloDisplay(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                  Instrução / Mensagem de Apoio
                </label>
                <input
                  type="text"
                  placeholder={getSubtituloExibicao()}
                  value={subtituloDisplay}
                  onChange={(e) => setSubtituloDisplay(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Cor do Destaque
                  </label>
                  <input
                    type="color"
                    value={corPrimaria}
                    onChange={(e) => setCorPrimaria(e.target.value)}
                    style={{ width: '50px', height: '36px', border: 'none', cursor: 'pointer', background: 'transparent' }}
                  />
                </div>

                {logoFinal && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', cursor: 'pointer', marginTop: '16px' }}>
                    <input
                      type="checkbox"
                      checked={incluirLogo}
                      onChange={(e) => setIncluirLogo(e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    Incluir Logo da Loja no QR Code
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pré-visualização do Display & Ações */}
        <div>
          <div 
            className="printable-card"
            style={{
              background: '#ffffff',
              border: `2px solid ${corPrimaria}`,
              borderRadius: '20px',
              padding: '30px 24px',
              textAlign: 'center',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.08)',
              position: 'relative'
            }}
          >
            {/* Cabeçalho do Card */}
            {logoFinal && (
              <img 
                src={logoFinal} 
                alt="Logo" 
                style={{ height: '48px', maxWidth: '140px', objectFit: 'contain', margin: '0 auto 12px auto', display: 'block' }} 
              />
            )}
            
            <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {getTituloExibicao()}
            </h2>
            
            <p style={{ fontSize: '13px', color: '#475569', margin: '0 auto 20px auto', maxWidth: '260px', lineHeight: '1.4' }}>
              {getSubtituloExibicao()}
            </p>

            {/* Canvas do QR Code */}
            <div 
              ref={qrRef} 
              style={{ 
                padding: '16px', 
                background: '#fff', 
                borderRadius: '16px', 
                border: '1px solid #e2e8f0', 
                display: 'inline-block',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
              }}
            >
              {qrValor ? (
                <QRCodeCanvas
                  value={qrValor}
                  size={210}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin={true}
                  imageSettings={
                    incluirLogo && logoFinal
                      ? {
                          src: logoFinal,
                          height: 38,
                          width: 38,
                          excavate: true,
                        }
                      : undefined
                  }
                />
              ) : (
                <div style={{ width: '210px', height: '210px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px' }}>
                  Preencha os dados para gerar o display
                </div>
              )}
            </div>

            {/* Informações de Apoio para Wi-Fi ou Mesa no rodapé do card */}
            {tipo === 'wifi' && wifiSsid && (
              <div style={{ marginTop: '16px', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155' }}>
                <div><strong>Rede:</strong> {wifiSsid}</div>
                {wifiSenha && <div><strong>Senha:</strong> {wifiSenha}</div>}
              </div>
            )}

            <div style={{ marginTop: '20px', borderTop: '1px dashed #cbd5e1', paddingTop: '14px', fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>
              Desenvolvido por Universo Mídia Digital
            </div>
          </div>

          {/* Botões de Ação (Não imprime) */}
          <div className="no-print" style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button
              onClick={imprimirDisplay}
              disabled={!qrValor}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: corPrimaria,
                color: '#fff',
                fontWeight: '600',
                fontSize: '14px',
                cursor: qrValor ? 'pointer' : 'not-allowed',
                opacity: qrValor ? 1 : 0.5,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Printer size={18} /> Imprimir Placa / Display
            </button>

            <button
              onClick={baixarQRCode}
              disabled={!qrValor}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 18px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#334155',
                fontWeight: '600',
                cursor: qrValor ? 'pointer' : 'not-allowed',
                opacity: qrValor ? 1 : 0.5
              }}
              title="Baixar apenas a imagem PNG"
            >
              <Download size={18} /> PNG
            </button>

            <button
              onClick={copiarConteudo}
              disabled={!qrValor}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#f8fafc',
                cursor: qrValor ? 'pointer' : 'not-allowed',
                opacity: qrValor ? 1 : 0.5
              }}
              title="Copiar link/código"
            >
              <Copy size={18} color="#475569" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default QRCodeManager;